import { FormEvent, useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Checkbox } from "~/components/ui/checkbox";
import { Spinner } from "~/components/ui/spinner";

const FAILURE_REASONS = [
  "E12 issues",
  "Bad start value",
  "Water droplets",
  "Unresponsive sensor(s)",
] as const;

const MANUAL_PREFIX = "[Manual] ";

export interface MarkFailedDialogProps {
  isOpen: boolean;
  currentReasons: string[];
  onCancel: () => void;
  onSubmit: (reasons: string[]) => void;
  onClear: () => void;
  error?: string;
  loading?: boolean;
}

export const MarkFailedDialog = ({
  isOpen,
  currentReasons,
  onCancel,
  onSubmit,
  onClear,
  error,
  loading,
}: MarkFailedDialogProps) => {
  const [selectedReasons, setSelectedReasons] = useState<Set<string>>(
    new Set()
  );
  const [otherReason, setOtherReason] = useState("");
  const [otherChecked, setOtherChecked] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Parse current reasons and pre-check any manual ones
      const manualReasons = currentReasons
        .filter((r) => r.startsWith(MANUAL_PREFIX))
        .map((r) => r.replace(MANUAL_PREFIX, ""));

      const predefinedSelected = new Set<string>();
      let otherValue = "";

      manualReasons.forEach((reason) => {
        if (FAILURE_REASONS.includes(reason as (typeof FAILURE_REASONS)[number])) {
          predefinedSelected.add(reason);
        } else {
          otherValue = reason;
        }
      });

      setSelectedReasons(predefinedSelected);
      setOtherReason(otherValue);
      setOtherChecked(otherValue.length > 0);
    }
  }, [currentReasons, isOpen]);

  const handleReasonToggle = (reason: string, checked: boolean) => {
    const newSet = new Set(selectedReasons);
    if (checked) {
      newSet.add(reason);
    } else {
      newSet.delete(reason);
    }
    setSelectedReasons(newSet);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const reasons: string[] = [];

    selectedReasons.forEach((reason) => {
      reasons.push(`${MANUAL_PREFIX}${reason}`);
    });

    if (otherChecked && otherReason.trim()) {
      reasons.push(`${MANUAL_PREFIX}${otherReason.trim()}`);
    }

    onSubmit(reasons);
  };

  const handleClear = () => {
    setSelectedReasons(new Set());
    setOtherReason("");
    setOtherChecked(false);
    onClear();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Mark Test as Failed</DialogTitle>
            <DialogDescription>
              Select the reasons why this test should be marked as failed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {FAILURE_REASONS.map((reason) => (
              <div key={reason} className="flex items-center space-x-2">
                <Checkbox
                  id={`reason-${reason}`}
                  checked={selectedReasons.has(reason)}
                  onCheckedChange={(checked) =>
                    handleReasonToggle(reason, Boolean(checked))
                  }
                />
                <Label htmlFor={`reason-${reason}`}>{reason}</Label>
              </div>
            ))}

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="reason-other"
                  checked={otherChecked}
                  onCheckedChange={(checked) => setOtherChecked(Boolean(checked))}
                />
                <Label htmlFor="reason-other">Other</Label>
              </div>
              {otherChecked && (
                <Input
                  placeholder="Enter custom reason..."
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  className="ml-6"
                />
              )}
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleClear}
              disabled={loading}
            >
              {loading ? <Spinner className="mr-2" /> : null}
              Clear reasons
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Spinner className="mr-2" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
